import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useDialog } from "@/lib/dialog";
import { AuthImg } from "@/components/AuthImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BusyLabel } from "@/components/loading";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const errMsg = (e) => e?.response?.data?.detail || "No se pudo completar la acción";
const uploadPrivate = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scope", "private");
  fd.append("kind", "transfer_receipt");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  return r.data.path;
};
const PAYPHONE_REASON = {
  no_payphone_id: "sin ID de Payphone",
  bundle_partial: "otros pedidos del mismo pago siguen activos",
  cutoff_expired: "pasó la ventana de reverso del mismo día",
  payphone_rejected: "Payphone rechazó el reverso",
};

const payLabel = (r) => (r.payment_method === "transfer"
  ? "Pagado por transferencia · el reembolso se hace por transferencia"
  : `Pagado con Payphone · reembolso manual${PAYPHONE_REASON[r.refund_manual_ineligibility] ? ` (${PAYPHONE_REASON[r.refund_manual_ineligibility]})` : ""}`);

const BankView = ({ b, title, testid }) => (
  <div className="bg-black/40 border border-[#262626] rounded p-3 text-xs text-neutral-300 space-y-0.5" data-testid={testid}>
    <div className="text-neutral-500 uppercase tracking-wider text-[10px] mb-1">{title}</div>
    <div>Banco: <b className="text-white">{b.bank || b.bank_name}</b> · {b.account_type}</div>
    <div>Cuenta: <b className="text-white">{b.account_number}</b></div>
    <div>Titular: <b className="text-white">{b.holder_name}</b></div>
    <div>Cédula titular: <b className="text-white">{b.holder_cedula}</b></div>
  </div>
);

function BankForm({ r, onDone, onCancel }) {
  const [f, setF] = useState({ bank: "", account_type: "ahorros", account_number: "", holder_name: "", holder_cedula: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/orders/${r.id}/refund-bank-info`, { ...f, source: "admin" }); toast.success("Datos bancarios guardados"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const field = (k, label) => (
    <div><Label className="text-xs text-neutral-400">{label}</Label>
      <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-[#171717] border-[#262626]" data-testid={`bank-${k}-${r.id}`} /></div>
  );
  return (
    <div className="mt-3 border-t border-[#262626] pt-3 grid sm:grid-cols-2 gap-2" data-testid={`bank-form-${r.id}`}>
      {field("bank", "Banco")}
      <div><Label className="text-xs text-neutral-400">Tipo de cuenta</Label>
        <select value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} className="w-full h-9 rounded-md bg-[#171717] border border-[#262626] px-2 text-sm" data-testid={`bank-account_type-${r.id}`}>
          <option value="ahorros">Ahorros</option><option value="corriente">Corriente</option>
        </select></div>
      {field("account_number", "N.º de cuenta")}
      {field("holder_name", "Titular")}
      {field("holder_cedula", "Cédula del titular")}
      <div className="flex items-end gap-2">
        <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`bank-save-${r.id}`}><BusyLabel busy={busy} text="Guardando…">Guardar datos</BusyLabel></Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

function ProofForm({ r, onDone, onCancel }) {
  const { confirm } = useDialog();
  const [f, setF] = useState({ transfer_receipt_path: "", receipt_number: "", transfer_date: "", note: "" });
  const [busy, setBusy] = useState(null);
  const cancelled = r.status === "CANCELADO";
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try { const p = await uploadPrivate(file); setF((x) => ({ ...x, transfer_receipt_path: p })); toast.success("Captura subida"); }
    catch { toast.error("No se pudo subir la captura"); } finally { setBusy(null); }
  };
  const submit = async () => {
    if (busy) return;
    if (!f.transfer_receipt_path || !f.receipt_number.trim()) { toast.error("Sube la captura y escribe el número de comprobante"); return; }
    if (f.note.trim().length < 5) { toast.error("Escribe una nota (mínimo 5 caracteres)"); return; }
    const desc = `Registra el reembolso de ${money(r.total)} (el total pagado) como YA transferido al comprador, con comprobante ${f.receipt_number}. No se le cobra nada al vendedor. No se puede deshacer.`;
    if (!(await confirm({ title: cancelled ? "¿Documentar el reembolso?" : "¿Cerrar el reembolso?", description: desc, confirmText: "Sí, confirmar" }))) return;
    setBusy("send");
    try {
      await api.post(`/admin/orders/${r.id}/${cancelled ? "refund-proof" : "manual-refund-close"}`, f);
      toast.success(cancelled ? "Reembolso documentado" : "Reembolso cerrado"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="mt-3 border-t border-[#262626] pt-3 space-y-2" data-testid={`proof-form-${r.id}`}>
      <div className="grid sm:grid-cols-3 gap-2">
        <div><Label className="text-xs text-neutral-400">Captura de la transferencia</Label>
          <Input type="file" accept="image/*,application/pdf" onChange={onFile} className="bg-[#171717] border-[#262626]" data-testid={`proof-file-${r.id}`} />
          {f.transfer_receipt_path && <div className="text-[10px] text-emerald-400 mt-1">✓ subida</div>}</div>
        <div><Label className="text-xs text-neutral-400">N.º de comprobante</Label>
          <Input value={f.receipt_number} onChange={(e) => setF({ ...f, receipt_number: e.target.value })} className="bg-[#171717] border-[#262626]" data-testid={`proof-number-${r.id}`} /></div>
        <div><Label className="text-xs text-neutral-400">Fecha</Label>
          <Input type="date" value={f.transfer_date} onChange={(e) => setF({ ...f, transfer_date: e.target.value })} className="bg-[#171717] border-[#262626]" data-testid={`proof-date-${r.id}`} /></div>
      </div>
      <div><Label className="text-xs text-neutral-400">Nota (obligatoria, queda en auditoría)</Label>
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="bg-[#171717] border-[#262626]" data-testid={`proof-note-${r.id}`} /></div>
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`proof-submit-${r.id}`}>
          <BusyLabel busy={busy === "send"} text="Guardando…">{cancelled ? "Guardar comprobante" : "Confirmar y cerrar reembolso"}</BusyLabel>
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

function RefundRow({ r, reload }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelled = r.status === "CANCELADO";
  const saved = r.buyer_saved_bank;
  const useSaved = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${r.id}/refund-bank-info`, { bank: saved.bank, account_type: saved.account_type, account_number: saved.account_number, holder_name: saved.holder_name, holder_cedula: saved.holder_cedula, source: "buyer_profile" });
      toast.success("Se usaron los datos de la ficha del comprador"); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const done = () => { setOpen(null); reload(); };
  return (
    <div data-testid={`manual-refund-${r.id}`} className={`bg-[#111] border rounded-md p-4 ${cancelled ? "border-red-500/40" : "border-amber-500/40"}`}>
      <div className="text-sm">Pedido <a href={`/order/${r.id}`} className="text-[#EF4444] font-mono hover:underline">#{r.id.slice(0, 8)}</a> · <b className="text-white">{money(r.total)}</b>
        <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-400" data-testid={`refund-state-${r.id}`}>{cancelled ? "Cancelado · falta comprobante del reembolso" : r.refund_status === "awaiting_transfer" ? "Datos listos · pendiente transferir" : "Faltan datos bancarios"}</span></div>
      <div className="text-xs text-neutral-400 mt-1">Comprador: <b className="text-white">{r.buyer?.name}</b> · {r.buyer?.email}</div>
      <div className="text-xs text-neutral-400">Vendedor: <b className="text-white">{r.seller?.name}</b> · {r.seller?.email}</div>
      <div className="text-xs text-neutral-500 mt-1">Motivo: {r.refund_reason || r.cancel_reason}</div>
      <div className="text-[11px] text-amber-300 mt-1" data-testid={`refund-paymethod-${r.id}`}>{payLabel(r)}</div>
      {r.payment_method === "transfer" && r.original_payment?.receipt_number && (
        <div className="text-[11px] text-neutral-400 mt-1" data-testid={`original-payment-${r.id}`}>
          Pago original: {r.original_payment.bank || "banco no indicado"} · comprobante {r.original_payment.receipt_number}
          {r.original_payment.receipt_path && <AuthImg path={r.original_payment.receipt_path} width={300} className="mt-1 h-24 w-auto rounded border border-[#262626]" />}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-2 mt-3">
        {r.refund_bank_info && <BankView b={r.refund_bank_info} title={`Datos del reembolso${r.refund_bank_info.source === "buyer_profile" ? " (de la ficha del comprador)" : r.refund_bank_info.entered_by_admin ? " (ingresados por admin)" : ""}`} testid={`refund-bank-${r.id}`} />}
        {!cancelled && !r.refund_bank_info && saved?.account_number && <BankView b={saved} title="Ficha del comprador (datos bancarios guardados)" testid={`saved-bank-${r.id}`} />}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {!cancelled && !r.refund_bank_info && saved?.account_number && (
          <Button size="sm" variant="outline" disabled={busy} onClick={useSaved} data-testid={`use-saved-bank-${r.id}`}><BusyLabel busy={busy} text="Guardando…">Usar estos datos</BusyLabel></Button>
        )}
        {!cancelled && (
          <Button size="sm" variant="outline" onClick={() => setOpen("bank")} data-testid={`enter-bank-${r.id}`}>{r.refund_bank_info ? "Cambiar datos bancarios" : "Ingresar datos bancarios"}</Button>
        )}
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen("proof")} data-testid={`${cancelled ? "document-refund" : "oob-close"}-${r.id}`}>
          {cancelled ? "Documentar reembolso" : "Marcar reembolso manual hecho y cerrar"}
        </Button>
      </div>
      {open === "bank" && <BankForm r={r} onDone={done} onCancel={() => setOpen(null)} />}
      {open === "proof" && <ProofForm r={r} onDone={done} onCancel={() => setOpen(null)} />}
    </div>
  );
}

function FailedSettlements() {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/ledger/failed").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const retry = async (id) => {
    setBusyId(id);
    try { await api.post(`/admin/ledger/retry/${id}`); toast.success("Liquidación creada"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };
  if (!rows.length) return null;
  return (
    <div className="mb-4 border border-red-500/50 bg-red-500/10 rounded p-3" data-testid="failed-settlements">
      <div className="text-sm text-red-300 font-semibold mb-2">Liquidaciones fallidas ({rows.length}) · revisar</div>
      {rows.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-2 text-xs text-neutral-300 py-1" data-testid={`failed-settlement-${o.id}`}>
          <span className="font-mono">#{o.id.slice(0, 8)}</span><span>{money(o.total)}</span><span>{o.status}</span>
          <span className="text-neutral-500 flex-1">{o.ledger_failed_context || ""} · {o.ledger_error}</span>
          <Button size="sm" variant="outline" disabled={busyId === o.id} onClick={() => retry(o.id)} data-testid={`retry-settlement-${o.id}`}>Reintentar liquidación</Button>
        </div>
      ))}
    </div>
  );
}

export function ManualRefundsPanel() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/manual-refunds").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return <div className="text-neutral-500 text-sm">Cargando devoluciones…</div>;
  return (
    <div data-testid="manual-refunds-panel">
      <h2 className="font-display text-xl mb-1">Devoluciones manuales ({list.length})</h2>
      <p className="text-xs text-neutral-500 mb-4">El comprador recibe el total que pagó. Toda acción pide prueba y queda en auditoría.</p>
      <FailedSettlements />
      {list.length === 0 && <div className="text-neutral-500 text-sm">Sin devoluciones manuales pendientes.</div>}
      <div className="space-y-3">{list.map((r) => <RefundRow key={r.id} r={r} reload={load} />)}</div>
    </div>
  );
}
